/** Contenido de un formato de compra → unidad base de inventario (Kg, L, UN). */

const GALLON_LITERS = 4;

const BASE_OR_CONTAINER = new Set([
  'kg',
  'kgs',
  'kilo',
  'kilos',
  'l',
  'lt',
  'lts',
  'litro',
  'litros',
  'un',
  'und',
  'uds',
  'u',
  'unidad',
  'unidades',
  'galon',
  'galones',
  'gl',
  'm',
  's',
  'caja',
  'cajas',
  'tineta',
  'tinetas',
  'saco',
  'sacos',
  'tambor',
  'tambores',
  'bidon',
  'bidones',
  'balde',
  'baldes',
  'lata',
  'latas',
  'rollo',
  'rollos',
]);

const foldUnit = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/¼/g, '1/4')
    .replace(/½/g, '1/2')
    .replace(/¾/g, '3/4')
    .replace(/\s+/g, ' ');

const parseNumber = (raw) => {
  const text = String(raw || '')
    .trim()
    .replace(/\s+/g, '')
    .replace(',', '.');
  if (!text) return null;
  if (text.includes('/')) {
    const [left, right] = text.split('/');
    const den = Number(right);
    const num = Number(left);
    if (!den || !Number.isFinite(num) || !Number.isFinite(den)) return null;
    const value = num / den;
    return value > 0 ? value : null;
  }
  const value = Number(text);
  return Number.isFinite(value) && value > 0 ? value : null;
};

export const packContentFromUnit = (unit) => {
  const folded = foldUnit(unit);
  if (!folded) return null;
  const compact = folded.replace(/[\s.]+/g, '');
  if (compact === 'galon' || compact === 'galones' || compact === 'gl') {
    return { factor: GALLON_LITERS, unit: 'L' };
  }
  if (BASE_OR_CONTAINER.has(compact)) return null;

  const mass = folded.match(
    /(\d+\s*\/\s*\d+|\d+(?:[.,]\d+)?)\s*(kg|kgs|kilos?|g|gr|gramos?|ton(?:elada)?s?)\b/
  );
  if (mass) {
    const amount = parseNumber(mass[1]);
    if (!amount) return null;
    const kind = mass[2];
    if (kind.startsWith('kg') || kind.startsWith('kilo')) return { factor: amount, unit: 'Kg' };
    if (kind.startsWith('ton')) return { factor: amount * 1000, unit: 'Kg' };
    return { factor: amount / 1000, unit: 'Kg' };
  }

  const volume =
    folded.match(/(\d+\s*\/\s*\d+|\d+(?:[.,]\d+)?)\s*(lts?|litros?|ml|cc)\b/) ||
    folded.match(/(\d+\s*\/\s*\d+|\d+(?:[.,]\d+)?)\s*l\b/);
  if (volume) {
    const amount = parseNumber(volume[1]);
    if (!amount) return null;
    const kind = volume[2] || 'l';
    if (String(kind).startsWith('ml') || kind === 'cc') return { factor: amount / 1000, unit: 'L' };
    return { factor: amount, unit: 'L' };
  }

  if (/\bgal(?:on(?:es)?)?s?\b|\bgl\b/.test(folded)) {
    const gallon =
      folded.match(/(\d+\s*\/\s*\d+|\d+(?:[.,]\d+)?)\s*(?:de\s+)?(?:gal(?:on(?:es)?)?s?|gl)\b/) ||
      folded.match(/(?:gal(?:on(?:es)?)?s?|gl)\s*(?:de\s+)?(\d+\s*\/\s*\d+|\d+(?:[.,]\d+)?)\b/);
    if (gallon) {
      const amount = parseNumber(gallon[1]);
      if (amount) return { factor: amount * GALLON_LITERS, unit: 'L' };
    }
  }

  const count =
    folded.match(/(\d+\s*\/\s*\d+|\d+(?:[.,]\d+)?)\s*(?:u\b|un\b|und\b|uds?\b|unidades?\b)/) ||
    folded.match(/\((\d+\s*\/\s*\d+|\d+(?:[.,]\d+)?)\s*(?:u|un|und|uds?|unidades?)\)/);
  if (count) {
    const amount = parseNumber(count[1]);
    if (amount) return { factor: amount, unit: 'UN' };
  }

  if (compact === 'par' || folded === 'par') return { factor: 2, unit: 'UN' };
  return null;
};

export const inventoryQtyFromPurchase = (qty, unit, formatCode = '') => {
  const parsed = packContentFromUnit(unit) || packContentFromUnit(formatCode);
  const amount = Number(qty || 0);
  if (!parsed) return { qty: amount, unit: String(unit || '').trim() || 'UN', factor: 1 };
  return { qty: amount * parsed.factor, unit: parsed.unit, factor: parsed.factor };
};

export const formatInventoryQty = (qty, unit, formatCode = '') => {
  const converted = inventoryQtyFromPurchase(qty, unit, formatCode);
  const formatted = Number(converted.qty || 0).toLocaleString('es-CL', { maximumFractionDigits: 2 });
  return converted.unit ? `${formatted} ${converted.unit}` : formatted;
};
